import { useOutletContext } from "react-router-dom";
import { useLang } from "@/hooks/useLang";
import { buildPath } from "@/lib/routes";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import LeadMagnet from "@/components/LeadMagnet";
import SocialLinks from "@/components/SocialLinks";
import { personSchema, breadcrumbSchema } from "@/lib/schema";

const CONTENT = {
  da: {
    title: ["Magnus Løv", "Schmidt"],
    tagline: "SEO specialist fra Odense — din partner i organisk vækst gennem strategisk SEO.",
    infobox: "Hurtige fakta",
    facts: [
      ["Fulde navn", "Magnus Løv Schmidt"],
      ["Titel", "SEO Specialist"],
      ["Bopæl", "Odense, Danmark"],
      ["Felt", "Søgemaskineoptimering"],
      ["Fokus", "Teknisk SEO, strategi, linkbuilding"],
    ],
    sections: [
      { id: "intro", h: "Introduktion", ps: [
        "Magnus Løv Schmidt er en dansk SEO specialist baseret i Odense. Han arbejder med søgemaskineoptimering på tværs af discipliner — fra teknisk SEO og on-page til linkbuilding og strategi — med ét gennemgående princip: SEO skal skabe reel forretningsværdi, ikke bare rankings.",
        "For Magnus handler SEO om mere end placeringer. Det handler om at skabe synlighed, der konverterer til omsætning, og om at oversætte kompleks data til handlekraftige strategier, der driver målbar vækst.",
      ]},
      { id: "baggrund", h: "Baggrund og karriere", ps: [
        "Magnus har arbejdet med SEO på tværs af mere end 15 brancher og analyseret næsten 100 websites. Den tværfaglige erfaring gør hans ekspertise bred: han forstår, hvordan SEO spiller sammen med resten af en virksomheds marketing og forretning.",
        "Gennem årene har han opbygget stærke samarbejder og været med til at skabe over en million organiske besøgende for de projekter og kunder, han har arbejdet med.",
      ]},
      { id: "ekspertise", h: "Ekspertise", ps: [
        "Magnus er generalist inden for SEO og har berørt alle flader af faget: teknisk SEO, on-page og off-page. Han bruger industristandardværktøjer som Google Search Console, Google Analytics, Ahrefs og Screaming Frog, og holder sig løbende opdateret gennem kilder som Search Engine Journal og Googles egen dokumentation.",
        "Hans tilgang er kendetegnet ved no-bullshit-rådgivning: han forklarer SEO i et sprog, alle kan følge med i, og fokuserer på det, der faktisk flytter forretningen.",
      ]},
      { id: "projekter", h: "Projekter", ps: [
        "Ved siden af sit rådgivningsarbejde er Magnus involveret i flere projekter, herunder LinksAsAService (linkbuilding som service), ShopSEO.agency (ecommerce SEO) og det kreative projekt Dreamprint.art. Projekterne fungerer også som et levende testmiljø, hvor nye strategier afprøves, før de anbefales til kunder.",
      ]},
      { id: "filosofi", h: "Filosofi", ps: [
        "Magnus tror på en holistisk tilgang, hvor SEO ses som én del af et større marketingbillede. Han lægger vægt på ærlighed, gennemsigtighed og på at være forrest inden for viden — altid med kundens forretning som det egentlige mål.",
        "Filosofien er enkel: en placering er kun værd noget, hvis den skaber værdi for forretningen. Derfor starter ethvert samarbejde med at forstå, hvordan du tjener penge — og slutter med målbare resultater, ikke tomme rapporter.",
      ]},
      { id: "samarbejde", h: "Sådan arbejder jeg med kunder", ps: [
        "Et samarbejde med mig starter altid med at forstå din forretning, dine mål og din nuværende situation. Derfra lægger jeg en prioriteret plan, hvor jeg tager de mest værdiskabende opgaver først. Du er med hele vejen og forstår, hvorfor jeg gør, som jeg gør.",
        "Jeg kan fungere som din faste SEO-partner, som sparringspartner på strategien eller som den, der klæder dit interne team på til selv at eksekvere. Uanset hvad er tilgangen den samme: konkret, ærlig og forankret i din forretning.",
      ]},
    ],
    table: {
      title: "Kompetencer og fokusområder",
      head: ["Disciplin", "Niveau", "Typiske værktøjer"],
      rows: [
        ["Teknisk SEO", "Ekspert", "Screaming Frog, Search Console"],
        ["On-page SEO", "Ekspert", "Ahrefs, Surfer, Frase"],
        ["Linkbuilding", "Erfaren", "Ahrefs, digital PR"],
        ["Ecommerce SEO", "Ekspert", "Semrush, struktureret data"],
        ["Strategi & analyse", "Ekspert", "GA4, Looker Studio"],
      ],
    },
  },
  en: {
    title: ["Magnus Løv", "Schmidt"],
    tagline: "SEO specialist from Odense — your partner in organic growth through strategic SEO.",
    infobox: "Quick facts",
    facts: [
      ["Full name", "Magnus Løv Schmidt"],
      ["Title", "SEO Specialist"],
      ["Based in", "Odense, Denmark"],
      ["Field", "Search engine optimisation"],
      ["Focus", "Technical SEO, strategy, link building"],
    ],
    sections: [
      { id: "intro", h: "Introduction", ps: [
        "Magnus Løv Schmidt is a Danish SEO specialist based in Odense. He works with search engine optimisation across disciplines — from technical SEO and on-page to link building and strategy — with one guiding principle: SEO should create real business value, not just rankings.",
        "For Magnus, SEO is about more than positions. It's about creating visibility that converts to revenue, and translating complex data into actionable strategies that drive measurable growth.",
      ]},
      { id: "background", h: "Background and career", ps: [
        "Magnus has worked with SEO across more than 15 industries and analysed nearly 100 websites. This cross-disciplinary experience makes his expertise broad: he understands how SEO plays together with the rest of a company's marketing and business.",
        "Over the years he has built strong partnerships and helped create over a million organic visitors for the projects and clients he has worked with.",
      ]},
      { id: "expertise", h: "Expertise", ps: [
        "Magnus is a generalist within SEO and has touched every surface of the craft: technical SEO, on-page and off-page. He uses industry-standard tools such as Google Search Console, Google Analytics, Ahrefs and Screaming Frog, and stays current through sources like Search Engine Journal and Google's own documentation.",
        "His approach is defined by no-bullshit advice: he explains SEO in a language everyone can follow and focuses on what actually moves the business.",
      ]},
      { id: "projects", h: "Projects", ps: [
        "Alongside his consulting work, Magnus is involved in several projects, including LinksAsAService (link building as a service), ShopSEO.agency (ecommerce SEO) and the creative project Dreamprint.art. The projects also serve as a living test environment where new strategies are tried before they're recommended to clients.",
      ]},
      { id: "philosophy", h: "Philosophy", ps: [
        "Magnus believes in a holistic approach where SEO is seen as one part of a larger marketing picture. He emphasises honesty, transparency and staying ahead on knowledge — always with the client's business as the real goal.",
        "The philosophy is simple: a ranking is only worth something if it creates business value. That's why every engagement starts by understanding how you make money — and ends with measurable results, not empty reports.",
      ]},
      { id: "working", h: "How I work with clients", ps: [
        "Working with me always starts by understanding your business, your goals and your current situation. From there we build a prioritised plan, tackling the most value-creating tasks first. You're involved all the way and understand why we do what we do.",
        "I can act as your ongoing SEO partner, a sparring partner on strategy, or the person who equips your internal team to execute themselves. Either way the approach is the same: concrete, honest and anchored in your business.",
      ]},
    ],
    table: {
      title: "Competencies and focus areas",
      head: ["Discipline", "Level", "Typical tools"],
      rows: [
        ["Technical SEO", "Expert", "Screaming Frog, Search Console"],
        ["On-page SEO", "Expert", "Ahrefs, Surfer, Frase"],
        ["Link building", "Experienced", "Ahrefs, digital PR"],
        ["Ecommerce SEO", "Expert", "Semrush, structured data"],
        ["Strategy & analytics", "Expert", "GA4, Looker Studio"],
      ],
    },
  },
};

export default function About() {
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  const c = CONTENT[lang];

  const meta = {
    da: { title: "Om Magnus Løv Schmidt | SEO Specialist i Odense", d: "Lær Magnus Løv Schmidt at kende — dansk SEO specialist fra Odense med 15+ brancher, 99+ analyserede sider og over 1 mio. skabte besøgende." },
    en: { title: "About Magnus Løv Schmidt | SEO Specialist in Odense", d: "Get to know Magnus Løv Schmidt — Danish SEO specialist from Odense with 15+ industries, 99+ analysed sites and over 1M visitors created." },
  }[lang];

  return (
    <>
      <Seo routeKey="about" lang={lang} title={meta.title} description={meta.d} image={profile?.avatar}
        jsonLd={[personSchema(profile || {}), breadcrumbSchema([{ name: t("nav_home"), path: buildPath("home", lang) }, { name: t("nav_about"), path: buildPath("about", lang) }])]} />
      <PageHero title={c.title} description={c.tagline}
        crumbs={[{ label: t("nav_home"), to: "home" }, { label: t("nav_about") }]} />

      <section className="px-6 md:px-12 lg:px-20 py-16">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* Content */}
          <div className="lg:col-span-8 space-y-12">
            {c.sections.map((s) => (
              <Reveal key={s.id}>
                <div id={s.id} className="scroll-mt-24 border-t border-border pt-8">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">{s.h}</h2>
                  {s.ps.map((p, i) => (
                    <p key={i} className="mt-4 text-muted-foreground font-light leading-relaxed">{p}</p>
                  ))}
                </div>
              </Reveal>
            ))}
            {c.table && (
              <Reveal>
                <div className="border-t border-border pt-8">
                  <h2 className="mb-6 font-heading text-2xl md:text-3xl font-bold tracking-tight">{c.table.title}</h2>
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-left text-sm" data-testid="about-table">
                      <thead>
                        <tr className="border-b border-border bg-secondary/50">
                          {c.table.head.map((h, i) => (
                            <th key={i} className="px-5 py-3 font-heading font-bold">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {c.table.rows.map((row, ri) => (
                          <tr key={ri} className="border-b border-border last:border-0">
                            {row.map((cell, ci) => (
                              <td key={ci} className={`px-5 py-3 ${ci === 0 ? "font-medium" : "text-muted-foreground"}`}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Reveal>
            )}
          </div>

          {/* Infobox + schema */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-5">
              <div className="overflow-hidden rounded-2xl border border-border bg-card">
                <div className="grayscale-hover aspect-square overflow-hidden">
                  <img src={profile?.avatar} alt="Danish SEO Specialist Magnus Løv Schmidt" className="h-full w-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="label-mono text-primary">{c.infobox}</div>
                  <dl className="mt-4 space-y-3">
                    {c.facts.map(([k, v]) => (
                      <div key={k} className="flex flex-col border-b border-border pb-3 last:border-0">
                        <dt className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/60">{k}</dt>
                        <dd className="mt-0.5 text-sm text-foreground">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-border bg-card p-6">
                <div className="label-mono mb-4 text-muted-foreground/60">{lang === "da" ? "Find mig her" : "Find me here"}</div>
                <SocialLinks columns="grid-cols-2" />
              </div>
            </div>
          </aside>
        </div>
      </section>
      <LeadMagnet profile={profile} />
    </>
  );
}
